'use client';

import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { LoaderCircle, BarChart2, TrendingUp, CircleCheckBig, Info, ChevronDown } from 'lucide-react';
import dynamic from 'next/dynamic';

const RankDistributionChart = dynamic(() => import('./RankDistributionChart'), { ssr: false });

interface MarksForm {
  physics: number;
  chemistry: number;
  biology: number;
  category: string;
}

interface PredictionResult {
  totalMarks: number;
  predictedAIR: { min: number; max: number; median: number };
  categoryRank: { min: number; max: number };
  percentile: number;
  expectedCategory: string;
  qualifies: boolean;
  cutoffMark: number;
}

/**
 * NEET 2024 Score → AIR mapping based on actual published data.
 * Each entry: [score, approximateAIR]
 * Source: NTA NEET 2024 result statistics
 */
const SCORE_RANK_TABLE: [number, number][] = [
  [720, 1],
  [715, 5],
  [710, 15],
  [700, 50],
  [690, 120],
  [680, 250],
  [670, 450],
  [660, 750],
  [650, 1100],
  [640, 1600],
  [630, 2200],
  [620, 3000],
  [610, 4000],
  [600, 5200],
  [590, 6800],
  [580, 8700],
  [570, 11000],
  [560, 13800],
  [550, 17000],
  [540, 21000],
  [530, 25500],
  [520, 30500],
  [510, 36500],
  [500, 43000],
  [490, 51000],
  [480, 60000],
  [470, 70000],
  [460, 82000],
  [450, 96000],
  [440, 112000],
  [430, 130000],
  [420, 150000],
  [410, 172000],
  [400, 197000],
  [390, 225000],
  [380, 256000],
  [370, 290000],
  [360, 328000],
  [350, 370000],
  [340, 415000],
  [330, 463000],
  [320, 514000],
  [310, 567000],
  [300, 620000],
  [290, 673000],
  [280, 725000],
  [270, 775000],
  [260, 820000],
  [250, 860000],
  [240, 895000],
  [230, 925000],
  [220, 950000],
  [210, 970000],
  [200, 985000],
  [180, 1000000],
];

// NEET 2024 qualifying cutoffs (minimum marks)
const QUALIFYING_CUTOFFS: Record<string, number> = {
  General: 164,
  EWS: 164,
  OBC: 129,
  SC: 129,
  ST: 129,
  PwD: 129,
};

// Category rank multipliers based on actual NEET 2024 seat distribution
const CATEGORY_RANK_MULTIPLIERS: Record<string, number> = {
  General: 1.0,
  EWS: 1.0,
  OBC: 0.68,
  SC: 0.42,
  ST: 0.25,
  PwD: 0.12,
};

function interpolateRank(score: number): number {
  if (score >= 720) return 1;
  if (score <= 180) return 1000000;

  for (let i = 0; i < SCORE_RANK_TABLE.length - 1; i++) {
    const [s1, r1] = SCORE_RANK_TABLE[i];
    const [s2, r2] = SCORE_RANK_TABLE[i + 1];
    if (score <= s1 && score >= s2) {
      const t = (s1 - score) / (s1 - s2);
      return Math.round(r1 + t * (r2 - r1));
    }
  }
  return 1000000;
}

function getPrediction(marks: MarksForm): PredictionResult {
  const total = Number(marks.physics) + Number(marks.chemistry) + Number(marks.biology);
  const medianAIR = interpolateRank(total);

  // ±8% variance for min/max range
  const minAIR = Math.max(1, Math.round(medianAIR * 0.92));
  const maxAIR = Math.round(medianAIR * 1.08);

  // Percentile calculation based on ~2.4 million candidates in NEET 2024
  const totalCandidates = 2400000;
  const percentile = Math.max(0.01, Math.min(99.99, ((totalCandidates - medianAIR) / totalCandidates) * 100));

  const catMult = CATEGORY_RANK_MULTIPLIERS[marks.category] || 1.0;
  const catMedian = Math.max(1, Math.round(medianAIR * catMult));

  const cutoffMark = QUALIFYING_CUTOFFS[marks.category] || 164;

  return {
    totalMarks: total,
    predictedAIR: { min: minAIR, max: maxAIR, median: medianAIR },
    categoryRank: {
      min: Math.max(1, Math.round(catMedian * 0.92)),
      max: Math.round(catMedian * 1.08),
    },
    percentile: Math.round(percentile * 100) / 100,
    expectedCategory: marks.category,
    qualifies: total >= cutoffMark,
    cutoffMark,
  };
}

const subjectInfo = [
  { id: 'sub-phy', key: 'physics' as const, label: 'Physics', max: 180, color: 'text-primary', barColor: 'bg-primary', placeholder: 'e.g. 145' },
  { id: 'sub-che', key: 'chemistry' as const, label: 'Chemistry', max: 180, color: 'text-accent', barColor: 'bg-accent', placeholder: 'e.g. 160' },
  { id: 'sub-bio', key: 'biology' as const, label: 'Biology (Botany + Zoology)', max: 360, color: 'text-success', barColor: 'bg-success', placeholder: 'e.g. 310' },
];

export default function RankPredictorClient() {
  const { register, handleSubmit, watch, formState: { errors } } = useForm<MarksForm>({
    defaultValues: { category: 'General' }
  });
  const [result, setResult] = useState<PredictionResult | null>(null);
  const [loading, setLoading] = useState(false);

  const watchedValues = watch(['physics', 'chemistry', 'biology']);
  const liveTotal = watchedValues.reduce((sum, v) => sum + (Number(v) || 0), 0);

  const onSubmit = async (data: MarksForm) => {
    setLoading(true);
    await new Promise(resolve => setTimeout(resolve, 1200));
    setResult(getPrediction(data));
    setLoading(false);
  };

  const getRankLabel = (rank: number) => {
    if (rank <= 50) return { label: 'AIIMS New Delhi Possible', color: 'text-success' };
    if (rank <= 200) return { label: 'Top AIIMS Campus', color: 'text-success' };
    if (rank <= 500) return { label: 'AIIMS + Top Central Colleges', color: 'text-success' };
    if (rank <= 1500) return { label: 'Top Govt Medical Colleges', color: 'text-accent' };
    if (rank <= 5000) return { label: 'Good Govt Colleges (AIQ)', color: 'text-accent' };
    if (rank <= 15000) return { label: 'State Quota Govt Colleges', color: 'text-warning' };
    if (rank <= 50000) return { label: 'State Quota + Private Colleges', color: 'text-warning' };
    if (rank <= 100000) return { label: 'Private / Deemed Colleges', color: 'text-danger' };
    return { label: 'Deemed / Management Quota', color: 'text-danger' };
  };

  return (
    <div className="max-w-screen-2xl mx-auto px-6 lg:px-8 py-10">
      {/* Header */}
      <div className="mb-10">
        <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-accent/8 rounded-full text-xs text-accent border border-accent/20 mb-4">
          <BarChart2 size={13} />
          <span>NEET 2025 Rank Predictor</span>
        </div>
        <h1 className="text-4xl sm:text-5xl font-extrabold text-foreground mb-3 tracking-tight">
          Predict Your <span className="gradient-text">NEET AIR</span>
        </h1>
        <p className="text-muted-foreground text-base max-w-xl">
          Enter your expected marks in each subject to get your predicted All India Rank, percentile, and category rank based on NEET 2024 data.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
        {/* Form */}
        <div className="lg:col-span-2">
          <div className="bg-white rounded-2xl p-6 border border-slate-200 shadow-sm">
            <h2 className="text-foreground font-semibold text-base mb-5">Enter Expected Marks</h2>
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
              {subjectInfo.map((sub) => {
                const watchVal = Number(watch(sub.key)) || 0;
                const pct = Math.min(100, (watchVal / sub.max) * 100);
                return (
                  <div key={sub.id}>
                    <div className="flex items-center justify-between mb-1.5">
                      <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                        {sub.label} <span className="text-danger">*</span>
                      </label>
                      <span className={`text-xs font-medium ${sub.color}`}>Max: {sub.max}</span>
                    </div>
                    <input
                      type="number"
                      {...register(sub.key, {
                        required: `${sub.label} marks required`,
                        min: { value: 0, message: 'Cannot be negative' },
                        max: { value: sub.max, message: `Max marks is ${sub.max}` },
                      })}
                      placeholder={sub.placeholder}
                      className="input-glass"
                    />
                    <div className="mt-1.5 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                      <div
                        className={`h-full rounded-full transition-all duration-300 ${sub.barColor} opacity-70`}
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                    {errors[sub.key] && <p className="text-danger text-xs mt-1">{errors[sub.key]?.message}</p>}
                  </div>
                );
              })}

              {/* Live total */}
              <div className="flex items-center justify-between p-3 rounded-xl bg-slate-50 border border-slate-200">
                <span className="text-sm text-muted-foreground font-medium">Live Total</span>
                <span className="text-2xl font-bold text-foreground tabular-nums">
                  {liveTotal}<span className="text-muted-foreground text-sm font-normal">/720</span>
                </span>
              </div>

              {/* Category */}
              <div>
                <label className="block text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1.5">
                  Category
                </label>
                <div className="relative">
                  <select {...register('category')} className="select-glass pr-8">
                    <option value="General">General (UR)</option>
                    <option value="OBC">OBC-NCL</option>
                    <option value="SC">SC</option>
                    <option value="ST">ST</option>
                    <option value="EWS">EWS</option>
                    <option value="PwD">PwD</option>
                  </select>
                  <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
                </div>
              </div>

              <button type="submit" disabled={loading} className="btn-primary w-full py-3 flex items-center justify-center gap-2 min-h-[48px]">
                {loading ? (
                  <><LoaderCircle size={16} className="animate-spin" /> Calculating Rank...</>
                ) : (
                  <><TrendingUp size={16} /> Predict My Rank</>
                )}
              </button>
            </form>

            <div className="mt-4 p-3 bg-amber-50 rounded-xl border border-amber-200 flex items-start gap-2">
              <Info size={14} className="text-warning flex-shrink-0 mt-0.5" />
              <p className="text-xs text-slate-600 leading-relaxed">
                Based on NEET 2024 score-rank distribution (~24 lakh candidates). Actual rank may vary ±8% depending on exam difficulty.
              </p>
            </div>
          </div>
        </div>

        {/* Results */}
        <div className="lg:col-span-3 space-y-5">
          {!result && !loading && (
            <div className="bg-white rounded-2xl p-12 border border-slate-200 shadow-sm flex flex-col items-center justify-center text-center min-h-[400px]">
              <div className="w-16 h-16 rounded-2xl bg-slate-100 flex items-center justify-center mb-4">
                <BarChart2 size={28} className="text-muted-foreground" />
              </div>
              <h3 className="text-foreground font-semibold text-lg mb-2">Your Rank Prediction</h3>
              <p className="text-muted-foreground text-sm max-w-xs">
                Enter your expected marks on the left to see your predicted All India Rank, percentile, and college eligibility.
              </p>
            </div>
          )}

          {loading && (
            <div className="bg-white rounded-2xl p-12 border border-slate-200 shadow-sm flex flex-col items-center justify-center text-center min-h-[400px]">
              <div className="w-16 h-16 rounded-2xl bg-primary/8 border border-primary/20 flex items-center justify-center mb-4">
                <LoaderCircle size={28} className="text-primary animate-spin" />
              </div>
              <p className="text-foreground font-semibold mb-2">Analysing Your Marks</p>
              <p className="text-muted-foreground text-sm">Cross-referencing with NEET 2024 rank data...</p>
            </div>
          )}

          {result && !loading && (
            <>
              {/* Qualification status */}
              <div className={`rounded-xl p-4 border flex items-center gap-3 ${result.qualifies ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
                <CircleCheckBig size={18} className={result.qualifies ? 'text-success' : 'text-danger'} />
                <div>
                  <p className={`text-sm font-semibold ${result.qualifies ? 'text-success' : 'text-danger'}`}>
                    {result.qualifies ? 'Qualifies NEET 2025' : 'Below Qualifying Cutoff'}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {result.expectedCategory} cutoff: {result.cutoffMark} marks · Your score: {result.totalMarks}
                  </p>
                </div>
              </div>

              {/* Main result card */}
              <div className="bg-white rounded-2xl p-6 border border-primary/20 shadow-sm relative overflow-hidden">
                <div className="floating-orb w-48 h-48 bg-primary/5 -top-10 -right-10" />
                <div className="relative z-10">
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-6">
                    <div className="bg-slate-50 rounded-xl p-4 border border-slate-200 text-center">
                      <p className="text-xs text-muted-foreground mb-1 uppercase tracking-wide">Total Score</p>
                      <p className="text-3xl font-extrabold text-foreground tabular-nums">{result.totalMarks}</p>
                      <p className="text-xs text-muted-foreground">out of 720</p>
                    </div>
                    <div className="bg-primary/5 rounded-xl p-4 border border-primary/20 text-center">
                      <p className="text-xs text-muted-foreground mb-1 uppercase tracking-wide">Predicted AIR</p>
                      <p className="text-2xl font-extrabold text-primary tabular-nums">
                        {result.predictedAIR.median.toLocaleString('en-IN')}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {result.predictedAIR.min.toLocaleString('en-IN')} – {result.predictedAIR.max.toLocaleString('en-IN')}
                      </p>
                    </div>
                    <div className="bg-accent/5 rounded-xl p-4 border border-accent/20 text-center">
                      <p className="text-xs text-muted-foreground mb-1 uppercase tracking-wide">Percentile</p>
                      <p className="text-3xl font-extrabold text-accent tabular-nums">{result.percentile}</p>
                      <p className="text-xs text-muted-foreground">out of 100</p>
                    </div>
                  </div>

                  {/* Category rank */}
                  <div className="flex items-center justify-between p-4 bg-amber-50 rounded-xl border border-amber-200 mb-4">
                    <div>
                      <p className="text-xs text-muted-foreground uppercase tracking-wide mb-0.5">{result.expectedCategory} Category Rank</p>
                      <p className="text-xl font-bold text-warning tabular-nums">
                        {result.categoryRank.min.toLocaleString('en-IN')} – {result.categoryRank.max.toLocaleString('en-IN')}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-muted-foreground mb-0.5">College Eligibility</p>
                      <p className={`text-sm font-semibold ${getRankLabel(result.predictedAIR.median).color}`}>
                        {getRankLabel(result.predictedAIR.median).label}
                      </p>
                    </div>
                  </div>

                  {/* Score breakdown */}
                  <div className="grid grid-cols-3 gap-3">
                    {[
                      { label: 'Physics', val: Number(watch('physics')) || 0, max: 180, color: 'bg-primary/50' },
                      { label: 'Chemistry', val: Number(watch('chemistry')) || 0, max: 180, color: 'bg-accent/50' },
                      { label: 'Biology', val: Number(watch('biology')) || 0, max: 360, color: 'bg-success/50' },
                    ].map((sub) => (
                      <div key={`breakdown-${sub.label}`} className="bg-slate-50 rounded-xl p-3 border border-slate-200">
                        <p className="text-xs text-muted-foreground mb-1">{sub.label}</p>
                        <p className="text-lg font-bold text-foreground tabular-nums">{sub.val}<span className="text-xs text-muted-foreground font-normal">/{sub.max}</span></p>
                        <div className="mt-2 h-1.5 bg-slate-200 rounded-full overflow-hidden">
                          <div className={`h-full rounded-full ${sub.color}`} style={{ width: `${(sub.val / sub.max) * 100}%` }} />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Chart */}
              <div className="bg-white rounded-2xl p-6 border border-slate-200 shadow-sm">
                <h3 className="text-foreground font-semibold text-base mb-4">Rank Distribution Analysis</h3>
                <RankDistributionChart predictedRank={result.predictedAIR.median} totalMarks={result.totalMarks} />
              </div>

              {/* Next steps */}
              <div className="bg-white rounded-2xl p-5 border border-slate-200 shadow-sm">
                <p className="text-foreground font-semibold text-sm mb-3">Next Steps</p>
                <div className="space-y-2">
                  {[
                    { action: 'Run full college prediction with this rank', href: '/neet-college-predictor', color: 'text-primary' },
                    { action: 'Compare your shortlisted colleges', href: '/compare-colleges', color: 'text-accent' },
                    { action: 'Book a free counselling session', href: '/student-dashboard', color: 'text-success' },
                  ].map((step) => (
                    <a key={`step-${step.action}`} href={step.href} className={`flex items-center gap-2 text-sm ${step.color} hover:opacity-80 transition-opacity`}>
                      <span>→</span>
                      <span>{step.action}</span>
                    </a>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}